import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid or missing reset token.");
      navigate("/login");
    }
  }, [token, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    try {
      setLoading(true);
      await api.resetPassword(token, newPassword);
      toast.success("Password has been successfully reset. You can now log in.");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password. The link might be expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <Card className="w-full max-w-md border-slate-200 rounded-sm shadow-lg animate-in fade-in zoom-in-95 duration-200">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 bg-slate-700 rounded-sm flex items-center justify-center mx-auto mb-4"><Lock className="w-6 h-6 text-white" /></div>
          <CardTitle className="text-2xl font-bold font-['Manrope']">Set New Password</CardTitle>
          <CardDescription>Enter your new password below.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="newPassword" className="text-slate-700">New Password</Label>
              <Input id="newPassword" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="Enter new password" className="mt-1.5 h-11 rounded-sm border-slate-300" disabled={loading} autoComplete="new-password" />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
              <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Confirm new password" className="mt-1.5 h-11 rounded-sm border-slate-300" disabled={loading} autoComplete="new-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-slate-700 hover:bg-slate-800 rounded-sm h-11">{loading ? "Resetting..." : "Reset Password"}</Button>
            <Button type="button" variant="ghost" onClick={() => navigate("/login")} disabled={loading} className="w-full text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4 mr-2" />Back to Login</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}